/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';
import { Redirect, type Route, type Routes } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Home } from '@atlassian/twg-microsite-experience';
import { BenchmarkRoute } from '@atlassian/twg-microsite-experience/benchmark-route';
import {
	BENCHMARK_REPORT_ROUTE_NAME,
	BENCHMARK_REPORT_ROUTE_PATH,
	BENCHMARK_ROUTE_NAME,
	BENCHMARK_ROUTE_PATH,
	BENCHMARK_REVIEW_ROUTE_NAME,
	BENCHMARK_REVIEW_ROUTE_PATH,
	BENCHMARK_RUN_ROUTE_NAME,
	BENCHMARK_RUN_ROUTE_PATH,
	CLI_ROUTE_NAME,
	CLI_ROUTE_PATH,
	HOME_ROUTE_NAME,
	HOME_ROUTE_PATH,
	GET_STARTED_ROUTE_NAME,
	GET_STARTED_ROUTE_PATH,
	type MicrositeRouteName,
} from '@atlassian/twg-microsite-experience/routes';

type MicrositeNamedRoute = {
	name: MicrositeRouteName;
	path: string;
	exact: true;
	component: Route['component'];
};

const homeRoute: MicrositeNamedRoute = {
	name: HOME_ROUTE_NAME,
	path: HOME_ROUTE_PATH,
	exact: true,
	component: Home,
};

// Lazily load the CLI page so its chunk is split out of the main bundle and is
// only fetched once `/cli` is actually routed to.
const CliPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_cli-page" */ '@atlassian/twg-microsite-experience/cli'
	).then((module) => ({ default: module.CliPage })),
);

const SetupPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_setup-page" */ '@atlassian/twg-microsite-experience/setup'
	).then((module) => ({ default: module.SetupPage })),
);

/**
 * Route component for `/cli`.
 *
 * This route is deliberately ungated. `/cli` is served its own HTML document
 * (`src/cli/index.html`), which declares `robots: index, follow` and a
 * self-referencing `canonical=/cli` in the raw HTTP response, so a crawler or a
 * social unfurler treats it as an indexable page without executing any
 * JavaScript. A client-side gate cannot vary that: the gate resolver is
 * installed in the browser (`src/feature-flags`) and `hasBifrostTemplate: false`
 * leaves nothing server-side able to change the `<head>`. Gating the component
 * would therefore publish an indexable URL that redirects every real visitor to
 * `/`, so the two have to agree, and the document is the half that cannot be
 * conditional.
 */
const CliRoute = (): JSX.Element => (
	<Suspense fallback={null}>
		<CliPage />
	</Suspense>
);

const cliRoute: MicrositeNamedRoute = {
	name: CLI_ROUTE_NAME,
	path: CLI_ROUTE_PATH,
	exact: true,
	component: CliRoute,
};

// Which benchmark view a visitor gets (the benchmark, the waitlist or a
// redirect) is decided in `@atlassian/twg-microsite-experience/benchmark-route`.
// `/benchmark` intentionally uses the home HTML document rather than publishing
// a dedicated frontend entry.
const benchmarkRoute: MicrositeNamedRoute = {
	name: BENCHMARK_ROUTE_NAME,
	path: BENCHMARK_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

// A run, its review and its report render the same page, which reads the view
// it owes from the route it was matched on. All four share the home HTML
// document, and must share the one component too: starting a run navigates
// from `/benchmark` to the run while the start is in flight, and a different
// component would remount the page and lose the run being started.
const benchmarkRunRoute: MicrositeNamedRoute = {
	name: BENCHMARK_RUN_ROUTE_NAME,
	path: BENCHMARK_RUN_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

const benchmarkReviewRoute: MicrositeNamedRoute = {
	name: BENCHMARK_REVIEW_ROUTE_NAME,
	path: BENCHMARK_REVIEW_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

const benchmarkReportRoute: MicrositeNamedRoute = {
	name: BENCHMARK_REPORT_ROUTE_NAME,
	path: BENCHMARK_REPORT_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

const SetupRoute = (): JSX.Element =>
	fg('twg_microsite_cli_mcp_setup_page') ? (
		<Suspense fallback={null}>
			<SetupPage />
		</Suspense>
	) : (
		<Redirect to={HOME_ROUTE_PATH} />
	);

const setupRoute: MicrositeNamedRoute = {
	name: GET_STARTED_ROUTE_NAME,
	path: GET_STARTED_ROUTE_PATH,
	exact: true,
	component: SetupRoute,
};

const catchAllRoute = {
	name: 'not-found-redirect',
	path: '*', // Matches anything not caught above
	component: () => <Redirect to="/" />, // Redirect to home
};

export const routes: Routes = [
	homeRoute,
	cliRoute,
	benchmarkRoute,
	benchmarkRunRoute,
	benchmarkReviewRoute,
	benchmarkReportRoute,
	setupRoute,
	catchAllRoute,
] as const;
